/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   BenchmarksPanel.tsx
 *     Feature         :   216-ui-cleanup (T027/T028 / FR-012)
 *     Description     :   Collection benchmarks — replaces the zero-value "Jobs by Type"
 *                         tile with actionable throughput/runtime/volume stats from
 *                         dbo.data_collection_runs (entities/sec·min·hr, runtime trend,
 *                         per-collector breakdown). "Something meaningful I can take back
 *                         and improve upon" — the owner's ask.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { apiClient } from '../../services/apiClient';
import { Card, DataTable, LoadingState, NotCollected, Select, chartTooltipStyle } from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

interface TierBenchmark {
  tier_name: string;
  runs: number;
  completed: number;
  failed: number;
  avg_duration_seconds: number | null;
  total_entities: number;
  entities_per_second: number | null;
  entities_per_minute: number | null;
  entities_per_hour: number | null;
  last_run_at: string | null;
}

interface CollectorBenchmark {
  collector_name: string;
  tier_name: string | null;
  runs: number;
  avg_duration_seconds: number | null;
  avg_entities_per_run: number | null;
  total_entities: number;
  entities_per_second: number | null;
  last_run_at: string | null;
}

/** Average lane tier-execution runtime for one tier on one day (owner 2026-10-04: the trend is per tier). */
export interface TrendPoint { day: string; tier_name: string; runs: number; avg_duration_seconds: number | null }

interface CollectionBenchmarks {
  windowDays: number;
  tiers: TierBenchmark[];
  collectors: CollectorBenchmark[];
  runtimeTrend: TrendPoint[];
}

/** Round ONCE, then split — rounding the seconds part on its own printed "16m 60s" for 1,019.6 s (prod 2026-10-04). */
export const fmtDuration = (s: number | null): string => {
  if (s == null) return '—';
  const r = Math.round(s);
  if (r < 60) return `${r}s`;
  if (r < 3600) return `${Math.floor(r / 60)}m ${r % 60}s`;
  return `${Math.floor(r / 3600)}h ${Math.floor((r % 3600) / 60)}m`;
};

/** 'YYYY-MM-DD' + n days, in UTC (the SQL groups by UTC date). */
const addDay = (day: string, n: number): string => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/**
 * One tier's line — a 53-s 5-min run and a 35-min daily must never share an average.
 * Every day from the first to the last is present: a day without runs is null (a gap in the line). The x-axis is a
 * category axis, so a missing day used to vanish and the line read as a steady trend (review 2026-10-05).
 */
export const trendForTier = (points: TrendPoint[], tier: string): Array<{ day: string; avg_duration_seconds: number | null }> => {
  const byDay = new Map(points.filter((p) => p.tier_name === tier).map((p) => [p.day, p.avg_duration_seconds]));
  const days = [...byDay.keys()].sort();
  if (days.length === 0) return [];
  const out: Array<{ day: string; avg_duration_seconds: number | null }> = [];
  for (let day = days[0]; day <= days[days.length - 1]; day = addDay(day, 1)) {
    out.push({ day, avg_duration_seconds: byDay.has(day) ? byDay.get(day) ?? null : null });
  }
  return out;
};

/** Fastest cadence first; a tier not listed here keeps a place after the known ones, A→Z. */
const TIER_ORDER = ['5min', '15min', '30min', 'hourly', '4x-daily', '2x-daily', 'daily', 'infra-sync'];
export const sortTiers = <T extends { tier_name: string }>(tiers: T[]): T[] => {
  const rank = (t: string) => { const i = TIER_ORDER.indexOf(t); return i < 0 ? TIER_ORDER.length : i; };
  return [...tiers].sort((a, b) => rank(a.tier_name) - rank(b.tier_name) || a.tier_name.localeCompare(b.tier_name));
};

/** The tier the trend opens on: daily when it has data, else the first tier that does. */
export const defaultTrendTier = (points: TrendPoint[]): string | null =>
  points.some((p) => p.tier_name === 'daily') ? 'daily' : (points[0]?.tier_name ?? null);

/** Per second, per minute, or per hour — a slow collector reads "8/hr", never "0/min" (review 2026-10-05). */
export const fmtRate = (t: { entities_per_second: number | null }): string => {
  const eps = t.entities_per_second;
  if (eps == null) return '—';
  if (eps >= 1) return `${eps.toFixed(1)}/s`;
  if (eps * 60 >= 1) return `${Math.round(eps * 60)}/min`;
  const perHour = Math.round(eps * 3600);
  return perHour < 1 ? '<1/hr' : `${perHour}/hr`;
};

export const BenchmarksPanel: React.FC = () => {
  const { data, isLoading } = useQuery<CollectionBenchmarks>({
    queryKey: ['collection-jobs', 'benchmarks'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: CollectionBenchmarks }>(
        '/api/operations/collection-jobs/benchmarks'
      );
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const [trendTier, setTrendTier] = useState<string | null>(null);

  const tierColumns: DataTableColumn<TierBenchmark>[] = [
    { key: 'tier_name', header: 'Tier', width: '16%', render: (t) => <span className="font-medium text-[var(--color-text-primary)]">{t.tier_name}</span> },
    { key: 'runs', header: 'Runs', width: '10%', align: 'right', render: (t) => `${t.completed}/${t.runs}` },
    { key: 'avg_duration_seconds', header: 'Avg Runtime', width: '14%', align: 'right', render: (t) => fmtDuration(t.avg_duration_seconds) },
    { key: 'total_entities', header: 'Work units', width: '14%', align: 'right', render: (t) => t.total_entities.toLocaleString() },
    {
      key: 'entities_per_second',
      header: 'Throughput',
      width: '16%',
      align: 'right',
      render: (t) =>
        t.entities_per_second == null ? (
          <span className="text-[var(--color-text-secondary)]" title="No work-bearing executions in the window">—</span>
        ) : (
          <span title={`${t.entities_per_minute}/min · ${t.entities_per_hour}/hr`}>{fmtRate(t)}</span>
        ),
    },
    {
      key: 'failed',
      header: 'Failed',
      width: '10%',
      align: 'right',
      render: (t) => (
        <span className={t.failed > 0 ? 'font-medium text-[var(--color-status-danger)]' : 'text-[var(--color-text-secondary)]'}>
          {t.failed}
        </span>
      ),
    },
  ];

  const collectorColumns: DataTableColumn<CollectorBenchmark>[] = [
    { key: 'collector_name', header: 'Collector', width: '28%', render: (c) => <span className="font-medium text-[var(--color-text-primary)]">{c.collector_name}</span> },
    { key: 'runs', header: 'Runs', width: '8%', align: 'right', render: (c) => c.runs },
    { key: 'avg_duration_seconds', header: 'Avg Runtime', width: '14%', align: 'right', render: (c) => fmtDuration(c.avg_duration_seconds) },
    { key: 'avg_entities_per_run', header: 'Units/Run', width: '14%', align: 'right', render: (c) => (c.avg_entities_per_run != null ? Math.round(c.avg_entities_per_run).toLocaleString() : '—') },
    { key: 'total_entities', header: 'Total', width: '12%', align: 'right', render: (c) => c.total_entities.toLocaleString() },
    { key: 'entities_per_second', header: 'Throughput', width: '14%', align: 'right', render: (c) => fmtRate(c) },
  ];

  if (isLoading) {
    return (
      <Card className="mb-6">
        <LoadingState rows={4} />
      </Card>
    );
  }

  const hasData = (data?.tiers.length ?? 0) > 0;
  const trendTiers = sortTiers([...new Set((data?.runtimeTrend ?? []).map((p) => p.tier_name))].map((tier_name) => ({ tier_name })))
    .map((t) => t.tier_name);
  const selectedTier = trendTier && trendTiers.includes(trendTier) ? trendTier : defaultTrendTier(data?.runtimeTrend ?? []);
  const trend = selectedTier ? trendForTier(data?.runtimeTrend ?? [], selectedTier) : [];

  return (
    <Card className="mb-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          Collection Benchmarks ({data?.windowDays ?? 14} days)
        </h2>
        {!hasData && <NotCollected reason="No completed collection runs in the window yet" />}
      </div>

      {hasData && (
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Tiers count each worker/brain execution once, with that execution&apos;s own runtime. Collectors show their own
          time within it. Work units are what the lane counted (attempted steps and items).
        </p>
      )}

      {hasData && (
        <div className="space-y-6">
          {/* Per-tier throughput */}
          <DataTable
            columns={tierColumns}
            rows={sortTiers(data!.tiers)}
            rowKey={(t) => t.tier_name}
            showPagination={false}
            emptyMessage="No runs in the window"
          />

          {/* Runtime trend */}
          {trendTiers.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-4">
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                  Average runtime per day
                </h3>
                <Select
                  aria-label="Tier"
                  selectSize="sm"
                  value={selectedTier ?? ''}
                  onChange={(e) => setTrendTier(e.target.value)}
                  options={trendTiers.map((t) => ({ value: t, label: t }))}
                />
              </div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend}>
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtDuration(v)} width={70} />
                    <Tooltip
                      {...chartTooltipStyle}
                      formatter={(value) => [fmtDuration(typeof value === 'number' ? value : null), 'Avg runtime']}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg_duration_seconds"
                      stroke="var(--color-accent)"
                      strokeWidth={2}
                      dot={{ r: 3 }}   /* a day between two gaps has no line segment; the dot shows it */
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top collectors by volume */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">
              Collectors by volume
              {data!.collectors.length > 10 ? ` (top 10 of ${data!.collectors.length})` : ''}
            </h3>
            <DataTable
              columns={collectorColumns}
              rows={data!.collectors.slice(0, 10)}
              rowKey={(c) => c.collector_name}
              showPagination={false}
              emptyMessage="No per-collector runs recorded"
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default BenchmarksPanel;
